import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, MapPin } from "lucide-react";
import SelectField from "@/components/forms/select_field";
import CityCombobox from "@/components/forms/city_combobox";
import ButtonMain from "@/components/buttons/button_main";
import { useMarket, PROVINCES, marketForProvince } from "@/context/LocationContext";
import { citiesFor } from "@/data/canada_cities";

// The market picker: shown once to visitors outside Canada, and any time the
// NavTop search pill is tapped. Solid dark card, display headline.
export default function MarketPicker() {
  const { market, setMarket, stayType, setStayType, pickerOpen, setPickerOpen } = useMarket();

  const [province, setProvince] = useState(market?.province || "ON");
  const [city, setCity] = useState(market?.city || "Toronto");
  const [stay, setStay] = useState(stayType);

  // Re-seed the draft from the live choice every time the sheet opens.
  useEffect(() => {
    if (!pickerOpen) return;
    setProvince(market?.province || "ON");
    setCity(market?.city || "Toronto");
    setStay(stayType);
  }, [pickerOpen, market, stayType]);

  function pickProvince(next) {
    setProvince(next);
    setCity(citiesFor(next)[0] || "");
  }

  function submit() {
    const chosen = city.trim() || marketForProvince(province).city;
    setMarket({ city: chosen, province });
    setStayType(stay);
    setPickerOpen(false);
  }

  return (
    <AnimatePresence>
      {pickerOpen && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-end justify-center bg-black/50 p-0 sm:items-center sm:p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setPickerOpen(false)}
        >
          <motion.div
            className="max-h-[90dvh] w-full max-w-md overflow-y-auto rounded-t-3xl border border-night-line bg-night-surface p-7 pb-[calc(1.75rem+env(safe-area-inset-bottom))] shadow-ambient sm:max-h-none sm:rounded-3xl sm:pb-7"
            initial={{ y: 80 }}
            animate={{ y: 0 }}
            exit={{ y: 120, opacity: 0 }}
            transition={{ type: "spring", stiffness: 320, damping: 30 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4">
              <span className="flex h-11 w-11 items-center justify-center rounded-full bg-night-tint text-brand-primary">
                <MapPin size={20} strokeWidth={1.9} />
              </span>
              <button
                type="button"
                aria-label="Close market picker"
                onClick={() => setPickerOpen(false)}
                className="rounded-full p-2 text-night-sub transition hover:bg-night-tint hover:text-night-text"
              >
                <X size={18} />
              </button>
            </div>

            <h2 className="mt-4 font-display text-2xl font-semibold tracking-tight text-night-text">
              Where are you looking?
            </h2>
            <p className="mt-1.5 text-sm text-night-sub">
              SwipRent lists homes across Canada. Pick your market.
            </p>

            <div className="mt-6 space-y-4">
              <SelectField
                label="Province or territory"
                options={PROVINCES}
                value={province}
                onChange={(e) => pickProvince(e.target.value)}
              />
              <CityCombobox
                label="City"
                provinceCode={province}
                value={city}
                onChange={setCity}
              />

              <div>
                <p className="mb-2 text-sm font-medium text-night-text">Stay type</p>
                <div className="flex gap-2">
                  {[
                    { value: "long", label: "Long term" },
                    { value: "short", label: "Short stay" },
                  ].map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setStay(opt.value)}
                      className={`flex-1 rounded-full border px-4 py-2.5 text-sm font-semibold transition ${
                        stay === opt.value
                          ? "border-brand-primary bg-brand-primary text-white"
                          : "border-night-line bg-night-surface text-night-sub hover:border-brand-primary hover:text-night-text"
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <ButtonMain full className="mt-6" onClick={submit}>
              Show me homes
            </ButtonMain>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
